/**
 * Edad corregida a la fecha de inicio del tratamiento.
 *
 * La columna age_at_start_corrected de la BD guarda esta misma edad pero
 * congelada como texto ingles ("1 year, 6 months, 3 days"), asi que no sirve
 * para mostrar en otro idioma: se recalcula aqui desde las fechas, que si son
 * neutras, y que el API ya devuelve en todos los endpoints de paciente.
 */

// Las fechas llegan como 'YYYY-MM-DD'. Se parten a mano en vez de pasarlas por
// new Date(): esa forma las interpreta como UTC pero los getters devuelven hora
// local, y en husos negativos la edad salia un dia corta.
const parseDate = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ""));
  return m ? { y: +m[1], m: +m[2], d: +m[3] } : null;
};

const daysInMonth = (year, month) => new Date(year, month, 0).getDate();

export const correctedAgeParts = (patient) => {
  // En prematuros la edad se cuenta desde la fecha prevista de parto, no desde
  // la de nacimiento: es la edad corregida que usa el equipo clinico.
  const base = parseDate(
    patient?.is_premature ? patient?.expected_delivery_date : patient?.date_of_birth
  );
  const start = parseDate(patient?.treatment_start_date);
  if (!base || !start) return null;

  let years = start.y - base.y;
  let months = start.m - base.m;
  let days = start.d - base.d;

  if (days < 0) {
    months--;
    days += daysInMonth(start.y, start.m - 1);
  }
  if (months < 0) {
    years--;
    months += 12;
  }
  return years < 0 ? null : { years, months, days };
};

// Intl ya conoce las reglas de plural de cada idioma —el rumano tiene tres
// formas y el neerlandes no varia—, asi que no hace falta mantenerlas a mano en
// los ficheros de locale.
const unit = (locale, value, name) => {
  try {
    return new Intl.NumberFormat(locale, {
      style: "unit",
      unit: name,
      unitDisplay: "long",
    }).format(value);
  } catch {
    // Navegador sin formato de unidades: se degrada al ingles antes que romper.
    return `${value} ${name}${value === 1 ? "" : "s"}`;
  }
};

export const formatCorrectedAge = (locale, patient) => {
  const parts = correctedAgeParts(patient);
  if (!parts) return "";

  const out = [];
  if (parts.years) out.push(unit(locale, parts.years, "year"));
  if (parts.months) out.push(unit(locale, parts.months, "month"));
  if (parts.days) out.push(unit(locale, parts.days, "day"));
  return out.length ? out.join(", ") : unit(locale, 0, "day");
};
